import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import Icon from 'components/Icons/Icon';
import Alert from 'components/Common/Alert';

// Sample initial data - replace with your own
const rootId = 1;
const generateRandomId = () => Math.floor(Math.random() * 100);

const initialGroups = [
  {
    id: rootId,
    isParent: true,
    parent_id: null,
    name: 'Root Group',
    initialGroup: true,
    isRoot: true,
    isAllSites: false,
    children: [
      {
        id: generateRandomId(),
        parent_id: rootId,
        isParent: false,
        name: 'All Sites',
        initialGroup: true,
        isRoot: false,
        isAllSites: true,
        children: [],
      },
    ],
  },
];

// Utility: find a node and its parent in the tree
const findNodeAndParent = (tree, targetId, parent = null) => {
  for (const node of tree) {
    if (node.id === targetId) return { node, parent };
    if (node.children?.length) {
      const result = findNodeAndParent(node.children, targetId, node);
      if (result.node) return result;
    }
  }
  return { node: null, parent: null };
};

// Utility: find a node that is an initialGroup node
const isAllSitesGroup = (tree, targetId) => {
  for (const node of tree) {
    if (node.id === targetId) {
      return node.isAllSites === true;
    }
    if (node.children?.length) {
      const result = isAllSitesGroup(node.children, targetId);
      if (result) return true;
    }
  }
  return false;
};

// Utility: check if targetId is a descendant of sourceId (prevents dropping parent into its own child)
const isDescendant = (node, targetId) => {
  if (!node.children?.length) return false;
  for (const child of node.children) {
    if (child.id === targetId) return true;
    if (isDescendant(child, targetId)) return true;
  }
  return false;
};

// Utility: remove a node from the tree
const removeNode = (tree, targetId) => {
  return tree
    .filter((node) => node.id !== targetId)
    .map((node) => ({
      ...node,
      children: node.children ? removeNode(node.children, targetId) : [],
    }));
};

// Utility: add a node as a child of targetParentId (or root if null)
const addNodeToParent = (tree, parent_id, nodeToAdd) => {
  if (parent_id === null) {
    return [...tree, nodeToAdd];
  }
  return tree.map((node) => {
    if (node.id === parent_id) {
      return { ...node, children: [...(node.children ?? []), nodeToAdd] };
    }
    return {
      ...node,
      children: node.children ? addNodeToParent(node.children, parent_id, nodeToAdd) : [],
    };
  });
};

function TreeNode({ node, level, onDragStart, onDragOver, onDragLeave, onDrop, onAddChild, onEditNode, onNameChange, onDeleteNode, draggedId, dragOverId, editId }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const hasChildren = node.children?.length > 0;
  const isBeingDragged = draggedId === node.id;
  const isDropTarget = dragOverId === node.id;

  const handleSubmitNewGroup = (value) => {
    console.log(value.toString())
    const trimmed = newGroupName.trim();
    if (!trimmed) {
      setIsAdding(false);
      setNewGroupName('');
      return;
    }
    onAddChild(node.id, trimmed);
    setNewGroupName('');
    setIsAdding(false);
    setIsExpanded(true);
  };

  const handleDeleteWhenChildren = (deleteNode, id) => {
    Alert({
      text: "This group has children that will also be deleted. Delete group anyway?",
      icon: "warning",
      confirmButtonText: "Delete",
      showCancelButton: true,
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.value) {
        deleteNode(id);
        return;
      }
      return;
    });
  }

  const darkMode = useSelector((state) => state.app.darkMode)

  return (
    <div>
      <div
        draggable={node.initialGroup === false}
        onDragStart={(e) => {
          if (node.isDraggable === false) {
            e.preventDefault();
            return;
          }
          onDragStart(e, node.id);
        }}
        onDragOver={(e) => onDragOver(e, node.id)}
        onDragLeave={onDragLeave}
        onDrop={(e) => onDrop(e, node.id)}
        style={{
          cursor: node.initialGroup !== false ? 'default' : 'grab',
          display: 'flex',
          alignItems: 'center',
          padding: '8px 10px',
          marginLeft: `${level * 20}px`,
          marginBottom: '2px',
          borderRadius: '6px',
          color: node.isAllSites ? darkMode ? 'gray' : '#9ca3af' : 'white',
          backgroundColor: isDropTarget ? `${darkMode ? 'gray' : '#dbeafe'}` : 
            isBeingDragged ? `${darkMode ? 'gray' : '#f3f4f6'}` : 'transparent',
          border: isDropTarget ? '2px dashed #3b82f6' : '2px solid transparent',
          opacity: isBeingDragged ? 0.5 : 1,
          transition: 'background-color 0.15s ease, border-color 0.15s ease',
          userSelect: 'none',
        }}
        onMouseEnter={(e) => {
          if (!isDropTarget && !isBeingDragged) e.currentTarget.style.backgroundColor = `${darkMode ? 'gray' : '#f9fafb'}`;
        }}
        onMouseLeave={(e) => {
          if (!isDropTarget && !isBeingDragged) e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        {node.initialGroup === false ?
          <Icon className="fas fa-grip-vertical" style={{ color: '#9ca3af', marginRight: 8, flexShrink: 0, width: '13px' }} />
        :
          <span style={{ width: '20px' }} />
        }

        <Button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            marginRight: 8,
            cursor: hasChildren ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            visibility: hasChildren ? 'visible' : 'hidden',
          }}
        >
          <Icon
            style={{ color: '#bbc1cdff' }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#6b7280'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#bbc1cdff'}
            className={`fas ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`}
          />
        </Button>

        <Icon
          className={`fas ${isExpanded && hasChildren ? 'fa-folder-open' : 'fa-folder'}`}
          style={{
            color: isExpanded && hasChildren ? '#3b82f6' : '#6b7280',
            marginRight: 8,
            flexShrink: 0,
          }}
        />

        {editId === node.id ? (
          <input
            type="text"
            autoFocus
            value={node.name}
            onChange={(e) => e.target.value.length && onNameChange(node.id, e.target.value)}
            onBlur={() => onEditNode(null)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === 'Escape') {
                onEditNode(null);
              };
            }}
            style={{
              fontSize: 14,
              color: `${darkMode ? 'whitesmoke' : '#1f2937'}`,
              backgroundColor: `${darkMode ? 'slategray' : 'white'}`,
              flex: 1,
              border: 'solid 1px gray',
              borderRadius: '5px',
              padding: '5px',
              outline: 'none',
            }}
          />
        ) : (
          <span
            style={{
              fontSize: 14,
              color: `${darkMode ? 'whitesmoke' : '#1f2937'}`,
              flex: 1,
              cursor: 'pointer',
              border: 'none',
              padding: '0px',
            }}
            onClick={() => onEditNode(node?.id)}
          >
            {node.name}
          </span>
        )}

        {node.name !== 'All Sites' &&
          <>
          {/* Add group */}
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setIsAdding(true);
                setIsExpanded(true);
              }}
              title="Add child group"
              style={{
                background: 'none',
                border: '1px solid transparent',
                padding: '4px 8px',
                borderRadius: 4,
                cursor: 'pointer',
                color: '#6b7280',
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${darkMode ? 'gray' : '#eff6ff'}`;
                e.currentTarget.style.color = `${darkMode ? 'whitesmoke' : '#3b82f6'}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#6b7280';
              }}
            >
              <Icon className="fas fa-plus" />
            </Button>

            {/* Delete group */}
            {!node.initialGroup &&
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  hasChildren ? 
                    handleDeleteWhenChildren(onDeleteNode, node?.id)
                  :
                    onDeleteNode(node.id);
                }}
                title="Delete group"
                style={{
                  background: 'none',
                  border: '1px solid transparent',
                  padding: '4px 8px',
                  borderRadius: 4,
                  cursor: 'pointer',
                  color: `${darkMode ? 'gray' : '#6b7280'}`,
                  fontSize: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${darkMode ? 'gray' : '#eff6ff'}`;
                  e.currentTarget.style.color = 'red';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#6b7280';
                }}
              >
                <Icon className="fas fa-trash" />
              </Button>
            }
          </>
        }
      </div>

      {isAdding && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginLeft: `${(level + 1) * 20}px`,
            marginBottom: 4,
            padding: '6px 10px',
            gap: 6,
          }}
        >
          <Icon className="fas fa-folder" style={{ color: `${darkMode ? 'gray' : '#9ca3af'}`, flexShrink: 0 }} />
          <input
            autoFocus
            type="text"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmitNewGroup(e.target.value);
              if (e.key === 'Escape') {
                setIsAdding(false);
                setNewGroupName('');
              }
            }}
            onBlur={handleSubmitNewGroup}
            placeholder="New group name..."
            style={{
              flex: 1,
              padding: '4px 8px',
              border: '1px solid #d1d5db',
              borderRadius: 4,
              fontSize: 13,
              outline: 'none',
            }}
          />
        </div>
      )}

      {isExpanded && hasChildren && (
        <div>
          {node.children.map((child) => 
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onAddChild={onAddChild}
              onEditNode={onEditNode}
              onNameChange={onNameChange}
              onDeleteNode={onDeleteNode}
              draggedId={draggedId}
              dragOverId={dragOverId}
              editId={editId}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default function GroupTree({ setFormData, setAvailableGroups }) {
  const [groups, setGroups] = useState(initialGroups);
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [editText, setEditText] = useState(null);

  const flattenTree = (nodes, parent_id = rootId) => {
    return nodes.reduce((acc, node) => {
      const { children, ...rest } = node;
      acc.push({ ...rest, parent_id });
      if (children?.length) acc.push(...flattenTree(children, node.id));
      return acc;
    }, []);
  };

  // useEffect(() => {
  //   const copy = [...groups];
  //   console.log('copy: ', copy)
  //   const updateIsParent = () => {
  //     return copy.forEach(n => {
  //       if (n.children.length > 0) {
  //         n.isParent = true;
  //       }
  //     })
  //   }
  //   // const updated = updateIsParent();
  //   // setGroups(updated);
  // }, [groups?.length])

  useEffect(() => {
    const flattenedGroups = flattenTree([...groups]);
    const modifiedGroups = flattenedGroups
      .filter(group => !group.initialGroup)
      .map(({ id, name, parent_id }) => ({ id, name, parent_id }));

    setFormData((prev) => ({
      ...prev,
      group_nodes: modifiedGroups,
    }));

    setAvailableGroups(groups)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  const handleAddGroup = (parent_id, name) => {
    setGroups((prev) => {
      const newNode = {
        id: generateRandomId(),
        parent_id: parent_id,
        isParent: false,
        name,
        initialGroup: false,
        isRoot: false,
        children: [],
      };
      return addNodeToParent(prev, parent_id, newNode);
    });
  };

  const handleEditGroup = (targetId) => {
    if (!targetId) { 
      setEditText(null);
      return;
    };

    const node = findNodeAndParent(groups, targetId)?.['node'] ?? null;
    if (!node || node.initialGroup) { return };

    setEditText(node.id);

    return;
  };
  
  const handleNameChange = (id, newName) => {
    const updateName = (nodes) => nodes.map((node) => {
      if (node.id === id) return { ...node, name: newName };
      if (node.children?.length) return { ...node, children: updateName(node.children) };
      return node;
    });
    setGroups((prev) => updateName(prev));
  };

  const handleDeleteGroup = (id) => {
    setGroups((prev) => removeNode(prev, id))
  };

  const handleDragStart = (e, id) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, targetId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (!draggedId || draggedId === targetId) return;

    const { node: draggedNode } = findNodeAndParent(groups, draggedId);
    
    // checks for initial group nodes to ignore
    const targetNode = findNodeAndParent(groups, targetId);
    if (targetNode?.['node']?.isAllSites) { return };

    if (draggedNode && isDescendant(draggedNode, targetId)) return;

    setDragOverId(targetId);
  };

  const handleDragLeave = () => {
    setDragOverId(null);
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    e.stopPropagation();

    const invalidGroup = isAllSitesGroup(groups, targetId)
    
    if (!draggedId || draggedId === targetId || invalidGroup) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }

    const { node: draggedNode } = findNodeAndParent(groups, draggedId);

    if (!draggedNode || isDescendant(draggedNode, targetId)) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }

    const treeWithoutNode = removeNode(groups, draggedId);
    const newTree = addNodeToParent(treeWithoutNode, targetId, draggedNode);

    setGroups(newTree);
    setDraggedId(null);
    setDragOverId(null);
  };

  const handleRootDrop = (e) => {
    e.preventDefault();
    if (!draggedId) return;

    const { node: draggedNode, parent } = findNodeAndParent(groups, draggedId);
    if (!draggedNode || parent === null) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }

    const treeWithoutNode = removeNode(groups, draggedId);
    const newTree = addNodeToParent(treeWithoutNode, null, draggedNode);

    setGroups(newTree);
    setDraggedId(null);
    setDragOverId(null);
  };

  const darkMode = useSelector((state) => state.darkMode)

  return (
    <div
      style={{
        padding: 24,
        // backgroundColor: `${!darkMode ? 'white' : ''}`,
        border: '1px solid #e5e7eb',
        borderRadius: 8,
      }}
    >
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleRootDrop}
        style={{ minHeight: 100 }}
      >
        <></>
        {groups.map((group) => (
          <TreeNode
            key={group.id}
            node={group}
            level={0}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onAddChild={handleAddGroup}
            onEditNode={handleEditGroup}
            onNameChange={handleNameChange}
            onDeleteNode={handleDeleteGroup}
            draggedId={draggedId}
            dragOverId={dragOverId}
            editId={editText}
          />
        ))}

        {/* <div
          style={{
            marginTop: 16,
            padding: 16,
            border: '2px dashed #d1d5db',
            borderRadius: 6,
            textAlign: 'center',
            color: '#9ca3af',
            fontSize: 13,
            backgroundColor: '#fafafa',
          }}
        >
          Drop here to move to root level
        </div> */}
      </div>
    </div>
  );
}